import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Spin, theme } from 'antd';
import { SearchOutlined, CloseOutlined, CloseCircleFilled, LeftOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import { IconCircleDot } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IAccessoryCategory, IAccessoryFamily, IAccessoryType } from '../interfaces/heavyDutyMachinery/IAccessoryTaxonomy';
import { getAccessoryTaxonomy, findAccessoryTypeByCode } from '../services/AccessoryTaxonomyService';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface AccessoryTypePickerProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
}

type Level = 'categories' | 'families' | 'types';

/**
 * AccessoryCategory -> AccessoryFamily -> AccessoryType drill-down picker
 * (see IAccessoryTaxonomy.ts) -- single-select-only sibling of
 * TaxonomyPicker for the separate, parallel accessory-type taxonomy (see
 * that component's own doc comment for the shared drill-down/search
 * reasoning, mirrored here). Deliberately simpler than TaxonomyPicker:
 * no `multiple` mode (MachineryAccessory.AccessoryTypeCode is a single
 * optional field, not a checklist), no curated per-node icon maps (always
 * the generic IconCircleDot -- a deliberate scope trim, purely cosmetic),
 * no facetCounts (this taxonomy isn't wired into search/filter facets yet).
 *
 * Selects/stores AccessoryType.Code while displaying each node's already-
 * localized Name -- see IAccessoryTaxonomy.ts's own doc comment on the
 * Code/name split. Re-fetches the taxonomy if the app language changes
 * mid-session.
 */
const AccessoryTypePicker: React.FC<AccessoryTypePickerProps> = ({ value, onChange, disabled, placeholder, title }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<IAccessoryCategory[] | null>(null);
  const [level, setLevel] = useState<Level>('categories');
  const [activeCategory, setActiveCategory] = useState<IAccessoryCategory | null>(null);
  const [activeFamily, setActiveFamily] = useState<IAccessoryFamily | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    getAccessoryTaxonomy(i18n.language)
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const selectedType = useMemo(
    () => (categories && value ? findAccessoryTypeByCode(categories, value) : undefined),
    [categories, value]
  );

  const resetNavigation = () => {
    setLevel('categories');
    setActiveCategory(null);
    setActiveFamily(null);
    setQuery('');
  };

  const openPicker = () => {
    if (disabled) return;
    resetNavigation();
    setOpen(true);
  };

  const closePicker = () => setOpen(false);

  const selectType = (type: IAccessoryType) => {
    onChange?.(type.code);
    closePicker();
  };

  // Every {category, family, type} triple, flattened -- only built while searching (see TaxonomyPicker's own doc comment on this same tradeoff).
  const searchResults = useMemo(() => {
    if (!categories || !query.trim()) return null;
    const needle = query.trim().toLowerCase();
    const rows: { category: IAccessoryCategory; family: IAccessoryFamily; type: IAccessoryType }[] = [];
    categories.forEach((category) =>
      category.families.forEach((family) =>
        family.types.forEach((type) => {
          if (type.name.toLowerCase().includes(needle)) rows.push({ category, family, type });
        })
      )
    );
    return rows;
  }, [categories, query]);

  const renderRow = (icon: React.ReactNode, label: string, onClick: () => void, opts?: { selected?: boolean; showChevron?: boolean }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 6px',
        border: 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      {icon}
      <span style={{ flex: 1, fontSize: 15, color: token.colorText, fontWeight: opts?.selected ? 600 : 400 }}>{label}</span>
      {opts?.showChevron !== false && !opts?.selected && <RightOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />}
      {opts?.selected && (
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: token.colorPrimary,
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
      )}
    </button>
  );

  const iconCircle = () => (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: token.colorFillTertiary,
        flexShrink: 0,
      }}
    >
      <IconCircleDot size={19} color={token.colorTextSecondary} />
    </div>
  );

  const renderBackHeader = (label: string, onBack: () => void) => (
    <button
      type="button"
      onClick={onBack}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 6px 12px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        color: token.colorText,
        fontWeight: 600,
        fontSize: 15,
      }}
    >
      <LeftOutlined style={{ fontSize: 13 }} />
      {label}
    </button>
  );

  const renderBody = () => {
    if (!categories) {
      return (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin size="small" />
        </div>
      );
    }

    if (query.trim()) {
      if (!searchResults || searchResults.length === 0) {
        return (
          <div style={{ textAlign: 'center', padding: 24, color: token.colorTextTertiary, fontSize: 14 }}>
            {translate(t, TRANSLATIONS.TAXONOMY_PICKER_NO_RESULTS)}
          </div>
        );
      }
      const groups: { family: IAccessoryFamily; rows: { category: IAccessoryCategory; family: IAccessoryFamily; type: IAccessoryType }[] }[] = [];
      searchResults.forEach((row) => {
        let group = groups.find((g) => g.family.id === row.family.id);
        if (!group) {
          group = { family: row.family, rows: [] };
          groups.push(group);
        }
        group.rows.push(row);
      });
      return groups.map((group) => (
        <div key={group.family.id} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: token.colorTextTertiary, textTransform: 'uppercase', padding: '10px 6px 4px' }}>
            {group.family.name}
          </div>
          {group.rows.map(({ type }) =>
            renderRow(<span style={{ width: 38, flexShrink: 0 }} />, type.name, () => selectType(type), {
              selected: selectedType?.code === type.code,
              showChevron: false,
            })
          )}
        </div>
      ));
    }

    if (level === 'categories') {
      return categories.map((category) =>
        renderRow(iconCircle(), category.name, () => {
          setActiveCategory(category);
          setLevel('families');
        })
      );
    }

    if (level === 'families' && activeCategory) {
      return (
        <>
          {renderBackHeader(activeCategory.name, resetNavigation)}
          {activeCategory.families.map((family) =>
            renderRow(iconCircle(), family.name, () => {
              setActiveFamily(family);
              setLevel('types');
            })
          )}
        </>
      );
    }

    if (level === 'types' && activeFamily) {
      return (
        <>
          {renderBackHeader(activeFamily.name, () => {
            setActiveFamily(null);
            setLevel('families');
          })}
          {activeFamily.types.map((type) =>
            renderRow(<span style={{ width: 38, flexShrink: 0 }} />, type.name, () => selectType(type), {
              selected: selectedType?.code === type.code,
              showChevron: false,
            })
          )}
        </>
      );
    }

    return null;
  };

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: '100%',
          padding: '10px 14px',
          borderRadius: 10,
          border: `1px solid ${token.colorBorder}`,
          background: disabled ? token.colorBgContainerDisabled : token.colorBgContainer,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          fontSize: 14,
          color: selectedType ? token.colorText : token.colorTextTertiary,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {selectedType?.name ?? placeholder ?? translate(t, TRANSLATIONS.TAXONOMY_PICKER_DEFAULT_TITLE)}
        </span>
        {selectedType && !disabled && (
          <CloseCircleFilled
            style={{ fontSize: 13, color: token.colorTextQuaternary, flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(undefined);
            }}
          />
        )}
        <DownOutlined style={{ fontSize: 11, color: token.colorTextTertiary, flexShrink: 0 }} />
      </button>

      <Modal
        open={open}
        onCancel={closePicker}
        footer={null}
        closeIcon={<CloseOutlined />}
        title={title ?? translate(t, TRANSLATIONS.TAXONOMY_PICKER_DEFAULT_TITLE)}
        width="min(560px, 92vw)"
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto', padding: '4px 4px 0' } }}
      >
        <div style={{ marginBottom: 8 }}>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder ?? translate(t, TRANSLATIONS.TAXONOMY_PICKER_SEARCH_PLACEHOLDER)}
            prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
            allowClear
            autoComplete="off"
          />
        </div>
        {renderBody()}
      </Modal>
    </>
  );
};

export default AccessoryTypePicker;
